// #81 F2 (web) — a new account's first action (join a squad, claim a profile) can be answered with
// 402 free_subscription_required: the server's Free sign-up gate wants a PeakCommerce subscription
// first. The app saves the action, follows the server's handoffUrl (PEAKCOMMERCE_FREE_SIGNUP_URL —
// whichever journey wins, nothing here changes), and on return /account/setting-up waits for the
// subscription and REPLAYS the action, so the runner never retypes an invite code.

export type PendingFirstAction =
  | { kind: "join"; body: { inviteCode: string; name: string; goalRace: string | null } }
  | { kind: "claim"; body: { memberId: number; inviteCode: string } };

// Keyed by the Clerk user id and checked against the owner stored inside the record (Celeste, Kayla's
// ruling on !139): an action saved by one account must never replay as another that signs in on the
// same browser inside the window — that would join a squad with the first account's invite code.
const LEGACY_KEY = "pr.pendingFirstAction"; // pre-fix, unkeyed: never replayed, removed on sight
const keyFor = (userId: string) => `${LEGACY_KEY}.${userId}`;
const MAX_AGE_MS = 2 * 60 * 60 * 1000; // a journey abandoned for hours is not resumed silently

export const ENDPOINT: Record<PendingFirstAction["kind"], string> = {
  join: "/api/squads/join",
  claim: "/api/me/claim-profile",
};

/** Emily's #81 F3 string 2 — the temporary hand-off state, the only line that offers a retry. */
export const STILL_SETTING_UP = "We’re still setting up your account. Try again in a few minutes.";

/** The server's hand-off URL when a response is the Free gate's 402, else null. */
export function freeSignupHandoff(status: number | undefined, data: unknown): string | null {
  if (status !== 402 || !data || typeof data !== "object") return null;
  const d = data as { code?: unknown; handoffUrl?: unknown };
  return d.code === "free_subscription_required" && typeof d.handoffUrl === "string" && d.handoffUrl ? d.handoffUrl : null;
}

// The hand-off URL is kept with the action so the wait page's Try again can send the runner back
// into the same Free journey without losing what they were doing (Kayla's ruling: the product
// recovers itself; support is never the next step here). A record without one is not replayable.
type Saved = { userId: string; action: PendingFirstAction; savedAt: number; handoffUrl: string };

export function savePendingAction(userId: string, action: PendingFirstAction, handoffUrl: string, now = Date.now()): void {
  try {
    localStorage.removeItem(LEGACY_KEY);
    localStorage.setItem(keyFor(userId), JSON.stringify({ userId, action, savedAt: now, handoffUrl } satisfies Saved));
  } catch {
    /* storage unavailable: the runner redoes the action by hand after the journey */
  }
}

/** This account's pending action and its journey — or null, discarding anything not provably theirs. */
export function loadPending(userId: string | null | undefined, now = Date.now()): { action: PendingFirstAction; handoffUrl: string } | null {
  try {
    localStorage.removeItem(LEGACY_KEY); // an unkeyed pre-fix record has no proven owner
    if (!userId) return null;
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return null;
    const saved = JSON.parse(raw) as Partial<Saved>;
    const valid =
      saved?.userId === userId &&
      !!saved.action &&
      typeof saved.savedAt === "number" &&
      now - saved.savedAt <= MAX_AGE_MS &&
      typeof saved.handoffUrl === "string" &&
      !!saved.handoffUrl;
    if (!valid) {
      localStorage.removeItem(keyFor(userId));
      return null;
    }
    return { action: saved.action!, handoffUrl: saved.handoffUrl! };
  } catch {
    return null;
  }
}

export function clearPendingAction(userId: string | null | undefined): void {
  try {
    localStorage.removeItem(LEGACY_KEY);
    if (userId) localStorage.removeItem(keyFor(userId));
  } catch {
    /* nothing to clear */
  }
}

/** Save the action and leave for the Free journey. */
export function startFreeSignup(userId: string, handoffUrl: string, action: PendingFirstAction, go: (url: string) => void = (u) => window.location.assign(u)): void {
  savePendingAction(userId, action, handoffUrl);
  go(handoffUrl);
}
